import keycodes from 'keycodes'
import lodash from 'lodash'
import * as React from 'react'
import type {KeyboardEvent, MouseEvent} from 'react'

import {autobind} from '@base/decorators'
import {
  themeable,
  classNames,
  cloneCreateOrCallNode,
  cloneOrCreate,
  omitProps,
  pickPropsExcept,
} from '@base/react-utils'

import PopoversPortal from '@base-uikit/layers/PopoversPortal'
import DropdownBalloon from '@base-uikit/popovers/DropdownBalloon'
import PopoverTrigger, {
  POPOVER_TRIGGER_PROPS,
} from '@base-uikit/popovers/PopoverTrigger'
import {getSizeClassName} from '@base-uikit/styles/grid'

type FocusedType = boolean | undefined | null | string | undefined | null

type CalendarSelectControllerStateType = {
  opened: boolean
  focused: FocusedType
  filter: any | undefined | null
  placement: string | undefined | null
}

type CalendarSelectControllerPropsType = {
  value: any
  getUnivocalFilterMatch: Function
  initialFilter: any
  isUnivocalFilter: Function
  onFilterCleared: Function
  inputIsReadOnly: boolean
  disabled: boolean
  openOnMount: boolean
  closeOnValueChange: boolean
  onValueChange: Function
  onKeyDown: Function | undefined | null
  onBlur: Function
  isItemEqual: Function
  theme: any
  select: any
  popover: any
  popoverTrigger: any
  placeholder: string
  portal: any
  children: any
  clearable: any
  siblings: any
  width?: string | null | undefined
  height?: string | null | undefined
}

// eslint-disable-next-line react/no-unsafe
@themeable()
class CalendarSelectController extends React.PureComponent<
  CalendarSelectControllerPropsType,
  CalendarSelectControllerStateType
> {
  static defaultProps = {
    tabIndex: 0,
    multiple: false,
    closeOnValueChange: true,
    inputIsReadOnly: false,
    isItemEqual: () => false,
    initialFilter: undefined,
    isUnivocalFilter: () => false,
    showFilteringInput: 'auto',
    placement: 'bottom,top,left,right',
    closeOutOfView: true,
    closeOnOutsideClick: true,
    animation: 'dropdown',
    minWidth: '100%',
    allowShift: 0,
    alignX: 0,
    focusBoundary: true,
    popover: DropdownBalloon,
    popoverTrigger: PopoverTrigger,
    portal: PopoversPortal,
  }

  state: CalendarSelectControllerStateType = {
    opened: false,
    focused: false,
    filter: undefined,
    placement: undefined,
  }

  calendarRef: any
  inputRef?: HTMLInputElement = undefined

  get _theme() {
    // @ts-expect-error comes from decorator
    return this.theme
  }

  UNSAFE_componentWillMount() {
    const {openOnMount} = this.props
    if (openOnMount) {
      this.open(false)
    }
  }

  @autobind
  resetFilter() {
    const {initialFilter} = this.props
    if (initialFilter) {
      return initialFilter
    }
    return {
      mode: lodash.get(this.state, 'filter.mode'),
      filter: null,
    }
  }

  @autobind
  handlePopoverToggle(focused: FocusedType = true) {
    this.setState({
      opened: !this.state.opened,
      focused,
    })
  }

  @autobind
  handleInputClick(event: MouseEvent, focused: FocusedType = true) {
    if (
      this.props.disabled ||
      event.defaultPrevented ||
      // ignore clicks originated from hitting Enter
      (!event.pageX && !event.pageY)
    ) {
      return
    }
    event.preventDefault()
    this.handlePopoverToggle(focused)
  }

  @autobind
  handleKeyDown(event: KeyboardEvent, focused: FocusedType = true) {
    const {disabled, onKeyDown} = this.props
    if (disabled) {
      return
    }

    const {keyCode, defaultPrevented} = event

    if (!defaultPrevented) {
      if (keyCode === keycodes('esc') || keyCode === keycodes('return')) {
        // Enter key is handled too, so that pressing it on an open calendar
        // inside a form, does not submit that form. Instead it closes the date
        // picker, which is what user expects.
        if (this.state.opened) {
          event.preventDefault()
          this.close()
        }
      } else if (
        keyCode === keycodes('up') ||
        keyCode === keycodes('down') ||
        keyCode === keycodes('space')
      ) {
        if (!this.state.opened) {
          event.preventDefault()
          this.open(focused)
        }
      }
    }

    if (this.calendarRef) {
      this.calendarRef.handleKeyDown(event)
    }

    if (onKeyDown) {
      onKeyDown(event)
    }
  }

  @autobind
  handleCalendarKeyDown(event: KeyboardEvent) {
    const {onKeyDown} = this.props
    if (onKeyDown) {
      onKeyDown(event)
    }

    if (event.defaultPrevented) {
      return
    }

    if (event.keyCode === keycodes('esc')) {
      event.preventDefault()
      this.close()
    }
  }

  @autobind
  handleCalendarValueChange(value: string) {
    return this.handleValueChange(value, false)
  }

  @autobind
  handleValueChange(value: string, focused: FocusedType = true) {
    const {onValueChange, closeOnValueChange, isItemEqual} = this.props
    if (isItemEqual(this.props.value, value)) {
      this.setState({
        focused,
        filter: this.resetFilter(),
      })
      return
    }

    if (onValueChange) {
      this.setState(
        {
          focused,
          filter: this.resetFilter(),
        },
        () => {
          onValueChange(value, focused)
          if (closeOnValueChange) {
            this.close(focused)
          }
        }
      )
    }
  }

  @autobind
  handleFilterChange(filter: any, focused: FocusedType) {
    this.setState(
      {
        filter,
        focused,
      },
      () => this.open()
    )
  }

  @autobind
  handleFocus(focused: FocusedType = true) {
    const f = !!focused
    if (this.state.focused !== f) {
      this.setState({focused: f})
    }
  }

  @autobind
  handleBlur() {
    this.setState({focused: false})
  }

  @autobind
  handleInputChange(filter: any, focused: FocusedType = true) {
    const {isUnivocalFilter, getUnivocalFilterMatch, value} = this.props
    if (isUnivocalFilter(filter, this.state.filter, value)) {
      this.handleValueChange(getUnivocalFilterMatch(filter, value), focused)
      return
    }

    this.handleFilterChange(filter, focused)
  }

  @autobind
  handlePopoverPosition(position: {placement: string}) {
    const {placement} = position
    if (placement !== this.state.placement) {
      this.setState({placement})
    }
  }

  open(focused: FocusedType = true) {
    if (this.state.opened) {
      return
    }
    this.handlePopoverToggle(focused)
  }

  close(focused: FocusedType = false) {
    if (!this.state.opened) {
      return
    }
    this.handlePopoverToggle(focused)
  }

  renderCalendar() {
    const props = {
      value: this.props.value,
      filter: this.state.filter,
      padded: true,
      ...omitProps(this.props, [
        // @ts-ignore
        this.constructor.propTypes,
        'children',
        'theme',
        'className',
        'style',
        'tabIndex',
        'autoFocus',
        'siblings',
      ]),
      tabIndex: -1,
      onValueChange: this.handleCalendarValueChange,
      onKeyDown: this.handleCalendarKeyDown,
      isItemEqual: this.props.isItemEqual,
      ref: (r: any) => {
        this.calendarRef = r
      },
    }
    return cloneOrCreate(this.props.select, props)
  }

  @autobind
  renderDropdown(props: any) {
    return cloneOrCreate(this.props.popover, (componentProps = {}) => ({
      ...props,
      tabIndex: -1,
      // In case of smaller screens - instead of displaying scroll, display calendar on the right or left side of input thanks to configuration prop "placement: 'bottom,top,left,right'"
      style: {...props.style, maxHeight: undefined},
      children: this.renderCalendar(),
    }))
  }

  renderInput() {
    const {value, children, placeholder, inputIsReadOnly} = this.props
    const {filter, focused} = this.state

    const siblings =
      (this.props.siblings ? this.props.siblings + ',' : '') +
      ((this.state.opened && this.state.placement) || '')

    const className = lodash.get(children, 'props.className')

    const props = {
      ...lodash.pick(this.props, [
        'renderItem',
        'onItemRender',
        'mapItemProps',
        'onItemProps',
        'onValueClear',
        'tabIndex',
        'width',
        'height',
        'invalid',
        'disabled',
        'size',
        'color',
      ]),
      className,
      siblings,
      focused,
      placeholder,
      readOnly: inputIsReadOnly,
      onKeyDown: this.handleKeyDown,
      onValueChange: this.handleInputChange,
      onClick: this.handleInputClick,
      onToggle: this.handlePopoverToggle,
      isItemEqual: this.props.isItemEqual,
      clearable: this.props.clearable,
      onFocus: this.handleFocus,
      onBlur: this.handleBlur,
      value: filter,
      item: value,
      ref: (r: HTMLInputElement) => {
        this.inputRef = r
      },
    }

    return cloneCreateOrCallNode(this.props.children, props)
  }

  render() {
    const props = {
      ...pickPropsExcept(
        this.props,
        [/^data-/, 'style', ...POPOVER_TRIGGER_PROPS],
        // width is a proper PopoverTrigger prop, but it's also passed to the SelectController for the input
        ['width', 'height']
      ),
      backdrop: null,
      className: classNames(getSizeClassName(this.props, this._theme)),
      opened: this.state.opened,
      popover: this.renderDropdown,
      onToggle: this.handlePopoverToggle,
      onPosition: this.handlePopoverPosition,
      portal: this.props.portal,
      children: this.renderInput(),
      closeOnOutsideClick: true,
    }

    return cloneOrCreate(this.props.popoverTrigger, props)
  }
}

export default CalendarSelectController
